/**
 * ORA - UNIFIED AI AGENT PAGE (Desktop View with Navigation)
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * 
 * ================================
 * DESKTOP NAVIGATION WRAPPER
 * ============================================================================
 * 
 * This component wraps UnifiedAIAgent with a desktop navigation bar featuring:
 * - "ORA Multi Agent Collaboration" branding
 * - Agent brain button (opens agent carousel)
 * - Hamburger menu launcher
 * 
 * Navigation adapts based on viewport:
 * - Desktop (>= 768px): Top navigation bar with full branding
 * - Mobile (< 768px): Simplified mobile header
 * ============================================================================
 */

import { useState, useEffect } from "react";
import { Menu, X, Bot, Brain } from "lucide-react";
import { UnifiedAIAgent } from "./UnifiedAIAgent";
import { AIAgentModal } from "./AIAgentModal";
import { CustomConfigurationsPage } from "./CustomConfigurationsPage";
import { AITrendsFeatureCard } from "./AITrendsFeatureCard";
import { UpgradeModal } from "./UpgradeModal";
import { useNavigation } from "@/contexts/NavigationContext";
import { GlobalFooter } from "./GlobalFooter";

interface UnifiedAIAgentPageProps {
  currentTier?: 'Free' | 'Pro' | 'Team' | 'Enterprise' | 'Custom';
}

export function UnifiedAIAgentPage({ 
  currentTier = "Free" 
}: UnifiedAIAgentPageProps) {
  const { navigate } = useNavigation();
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [showAILeadershipModal, setShowAILeadershipModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCustomConfigurations, setShowCustomConfigurations] = useState(false);
  const [showAITrends, setShowAITrends] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Handle deep links from URL parameters
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const agentType = params.get('agent');
      const view = params.get('view');
      const newSession = params.get('newSession') === 'true';
      
      console.log('🔍 URL Parameters detected:', { view, agentType, newSession });
      
      // Start fresh chat session if newSession parameter is set
      if (newSession) {
        console.log('🔄 Setting startFreshChat flag');
        localStorage.setItem('startFreshChat', 'true');
      }
      
      // Handle view=workforce parameter (opens workforce agent)
      if (view === 'workforce') {
        console.log('✅ Opening agent from view=workforce');
        setIsAgentOpen(true);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle agent=open parameter (opens workforce agent)
      else if (agentType === 'open') {
        console.log('✅ Opening agent from agent=open');
        setIsAgentOpen(true);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle agent=leadership parameter (opens leadership agent)
      else if (agentType === 'leadership') {
        console.log('✅ Opening leadership agent');
        setShowAILeadershipModal(true);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle agent=sme or agent=workforce parameter (opens workforce agent)
      else if (agentType === 'workforce' || agentType === 'sme') {
        console.log('✅ Opening agent from agent=workforce or agent=sme');
        setIsAgentOpen(true);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-black transition-colors">
      {/* Desktop Top Navigation */}
      <nav className="sticky top-0 z-40 bg-black shadow-xl transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            {/* Left: ORA Branding */}
            <div className="flex items-center">
              <h1 className="text-xl font-bold !text-white">
                ORA
              </h1>
            </div>

            {/* Right: Hamburger Menu */}
            <div className="flex items-center gap-2">
              {/* Hamburger Menu */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 rounded-lg transition-colors bg-gradient-to-r from-blue-700 to-blue-900 !text-white"
                title="Menu"
              >
                {showMobileMenu ? <X className="size-5 !text-white" /> : <Menu className="size-5 !text-white" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="border-t border-white bg-black">
            <div className="px-4 py-3 space-y-0">
              <button
                onClick={() => {
                  setIsAgentOpen(true);
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white"
              >
                <Bot className="size-5 fill-yellow-400 stroke-gray-700 stroke-2" />
                <span className="text-sm font-semibold !text-white">Open SME AI Agent</span>
              </button>
              
              <button
                onClick={() => {
                  navigate('/analytics');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white"
              >
                <span className="text-sm font-medium !text-white">Analytics Dashboard</span>
              </button>

              <button
                onClick={() => {
                  navigate('/configuration');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white"
              >
                <span className="text-sm font-medium !text-white">Configuration</span>
              </button>

              <button
                onClick={() => {
                  setShowCustomConfigurations(true);
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white"
              >
                <span className="text-sm font-medium !text-white">Integration Options</span>
              </button>

              <button
                onClick={() => {
                  setShowAITrends(true);
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white"
              >
                <span className="text-sm font-medium !text-white">AI Trends RAG</span>
              </button>

              <button
                onClick={() => {
                  setShowAILeadershipModal(true);
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white"
              >
                <span className="text-sm font-medium !text-white">AI Leadership</span>
              </button>

              <a
                href="https://agent.myora.now/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowMobileMenu(false)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white no-underline"
              >
                <span className="text-sm font-medium !text-white">Free AI Accelerator Training</span>
              </a>

              <a
                href="https://agent.myora.now/?action=create-video"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowMobileMenu(false)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white border-b border-white no-underline"
              >
                <span className="text-sm font-medium !text-white">Create Personal Avatar</span>
              </a>

              <button
                onClick={() => {
                  navigate('/unified-agent');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-gray-900 !text-white"
              >
                <span className="text-sm font-medium !text-white">Home</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 bg-black">
        {/* Hero Section - Mobile Optimized - Black Background */}
        <div className="bg-black py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] text-center leading-[1.2] flex flex-col items-center">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-1 sm:mb-2 tracking-normal !text-white -mt-3">O&nbsp;&nbsp;R&nbsp;&nbsp;A</h2>
          <p className="text-xs sm:text-sm mb-1 sm:mb-2 !text-gray-400 leading-tight tracking-widest">
            Observe &nbsp;&nbsp; Respond &nbsp;&nbsp; Act
          </p>
          <p className="text-sm sm:text-base lg:text-base font-semibold !text-white mb-4 sm:mb-5 max-w-2xl mx-auto leading-snug px-2 mt-[2px]">
            The key to unlocking AI value is in mobile<br />
            micro-learning: from hand-held capabilities<br />
            to cultural confidence and <span className="whitespace-nowrap">measurable change!</span>
          </p>
          <button
            onClick={() => setIsAgentOpen(true)}
            title="Launch AI Agent"
            className="flex flex-col items-center justify-center gap-1 px-5 sm:px-7 py-3 sm:py-5 bg-gradient-to-r from-magenta-500 to-blue-500 rounded-lg hover:shadow-lg hover:scale-105 transition-all font-bold !text-white shadow-[0_8px_16px_rgba(0,0,0,0.4)] border-4 border-white/40"
          >
            <Brain className="size-20 sm:size-24 fill-yellow-400 stroke-black stroke-2" />
            <span className="text-base sm:text-lg font-semibold">Tap to try now!</span>
          </button>
          <p className="text-sm sm:text-base mb-1.5 sm:mb-2 !text-gray-400 leading-snug mt-4 sm:mt-5">Stay in the black!<br />Learn the insider secrets for AI ROI!</p>
          <button
            onClick={() => navigate('/analytics')}
            className="inline-block mt-4 sm:mt-5 px-4 sm:px-5 py-1.5 sm:py-2 bg-gradient-to-r from-magenta-500 to-blue-500 text-xs sm:text-sm font-semibold !text-white rounded-lg transition-colors border-3 border-white/40 shadow-[0_8px_16px_rgba(0,0,0,0.4)] hover:shadow-lg cursor-pointer"
          >
            AI KPI Dashboard
          </button>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mt-2.5 sm:mt-3 lg:mt-4 leading-snug !text-white">
            Ask about digital persona agents!
          </h2>
        </div>

        {/* ORA Framework & CTA Section - Black Background */}
        <div className="bg-black py-2 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
          <div className="text-center max-w-4xl mx-auto space-y-6 sm:space-y-10">
            {/* CTA */}
            <div className="pt-3 sm:pt-4 border-t border-white/20">
              <p className="text-sm sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10 leading-relaxed px-2 !text-white">
                Start unlocking the full power of the digital environment while scaling capabilities with confidence, precision and measurable change!
              </p>
              <div className="flex flex-col gap-3 sm:gap-4 justify-center">
                <a
                  href="mailto:cara@oratf.info?subject=Request%20for%20Pricing"
                  className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-3.5 bg-gradient-to-r from-magenta-600 to-blue-600 font-semibold text-sm sm:text-base rounded-lg hover:shadow-lg transition-all text-center flex items-center justify-center gap-2 !text-white shadow-[0_8px_16px_rgba(0,0,0,0.4)] border border-white/20 no-underline hover:no-underline"
                >
                  Request Pricing
                </a>
                <a
                  href="https://calendly.com/caraz007"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-3.5 bg-gradient-to-r from-magenta-600 to-blue-600 font-semibold text-sm sm:text-base rounded-lg hover:shadow-lg transition-all text-center no-underline hover:no-underline !text-white shadow-[0_8px_16px_rgba(0,0,0,0.4)] border border-white/20"
                >
                  Book Live Q & A
                </a>
                <p className="text-sm sm:text-base !text-gray-400 mt-3 sm:mt-4">Connective Tissue with Context!</p>
                <div className="mt-3 sm:mt-4 flex items-center justify-center px-2">
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-magenta-600 to-blue-600 border border-white/20 rounded-lg shadow-[0_8px_16px_rgba(0,0,0,0.4)]">
                    <span className="text-xs sm:text-sm font-semibold text-white">Ideal for training, internal communications & modern branding!</span>
                  </div>
                </div>
                <p className="text-sm sm:text-base !text-white mt-2 sm:mt-3">ORA is the Premier Agile AI Leadership Framework.</p>
              </div>
            </div>
          </div>
        </div>
      </main>



      {/* Lower Section with Black Background */}
      <div className="bg-black shadow-xl py-8 sm:py-12 lg:py-16">
        {/* Unified AI Agent Modal */}
        <UnifiedAIAgent
          isOpen={isAgentOpen}
          onClose={() => setIsAgentOpen(false)}
          mode="desktop"
          theme="light"
          onThemeToggle={undefined}
          currentTier={currentTier}
          showNavigation={true}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />

        {/* AI Leadership Modal */}
        <AIAgentModal
          isOpen={showAILeadershipModal}
          onClose={() => setShowAILeadershipModal(false)}
        />

        {/* Custom Configurations Modal */}
        <CustomConfigurationsPage
          isOpen={showCustomConfigurations}
          onClose={() => setShowCustomConfigurations(false)}
          onUpgradeClick={() => navigate('/pricing')}
        />

        {/* AI Trends RAG Modal */}
        {showAITrends && (
          <AITrendsFeatureCard onClose={() => setShowAITrends(false)} />
        )}

        {/* Upgrade Modal - Rendered at top level to escape overlay z-index */}
        {showUpgradeModal && (
          <UpgradeModal 
            isOpen={showUpgradeModal} 
            onClose={() => setShowUpgradeModal(false)} 
          />
        )}

        {/* Global Footer */}
        <GlobalFooter />
      </div>

      {/* Floating Buttons - Mobile Optimized */}
      {/* Desktop: Side by side | Mobile: Stacked vertically */}
      <div className="fixed right-4 sm:right-6 flex flex-col gap-3 sm:gap-4 z-40 floating-buttons-above-mission">
        {/* Floating AI Trends RAG Button - Sparkly (appears first) */}
        <button
          onClick={() => setShowAITrends(true)}
          className="p-3 sm:p-3 bg-gradient-to-r from-blue-600 to-purple-700 text-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-2xl hover:shadow-purple-500/50 hover:scale-110 transition-all flex items-center gap-2 text-xs sm:text-sm font-semibold"
          aria-label="View AI Trends RAG Feature"
          title="New Feature"
        >
          <span className="text-[1.4375rem]">✨</span>
          <span className="hidden sm:inline">New</span>
        </button>

        {/* Floating Learn Button */}
        <button
          onClick={() => setShowAILeadershipModal(true)}
          className="p-3 sm:p-4 bg-gradient-to-r from-blue-700 via-blue-800 to-purple-700 text-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-2xl hover:shadow-purple-500/50 hover:scale-110 transition-all flex items-center gap-2 text-xs sm:text-sm font-semibold"
          aria-label="Learn More"
          title="Learn More"
        >
          <Brain className="size-9 sm:size-11 fill-yellow-300 stroke-gray-800" />
          <span className="hidden sm:inline">Learn</span>
        </button>
      </div>
    </div>
  );
}